import React, { useCallback, useLayoutEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import { RRule } from 'rrule';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AppView, Button, CardView, SettingsItemWithCheckBox } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { savePotsOnboardingState } from 'features/pots/actions';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import { useUpdateAutosaveCollectionFrequency } from 'features/pots/hooks/useUpdateAutosaveCollectionFrequency';
import { selectAutosaveCollectionMonthlyRRule, selectAutosaveRulesArr } from 'features/pots/reducers/selectors';
const AutosaveChooseMonthlyFrequency = withAnimatedNavigationHeader(({ route, scrollHandler, navigation }) => {
    const dispatch = useAppDispatch();
    const autosaveId = useAppSelector((state) => state.pots.savingsPlan?.id);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const autosaveCollectionMonthlyRRule = useAppSelector(selectAutosaveCollectionMonthlyRRule);
    const isEditFrequency = route.params && 'isEditFrequency' in route.params ? route.params.isEditFrequency : false;
    const { updateCollectionFrequency, fetching } = useUpdateAutosaveCollectionFrequency(true);
    const rules = useAppSelector(selectAutosaveRulesArr);
    const hasOnboardingState = useAppSelector((state) => !!state.pots.onboarding);
    const [state, setState] = useState(autosaveCollectionMonthlyRRule ? autosaveCollectionMonthlyRRule.type : 'FIRST_WORKING_DAY');
    const navigateToNextScreen = useNavigateToNextScreenForPotsOnboarding();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>,
        });
    }, []);
    const onSelectPeriod = useCallback((period) => () => {
        setState(period);
    }, []);
    const handleContinue = useCallback(() => {
        if (state === 'FIXED_MONTHLY_DATE') {
            navigation.navigateDeprecated('AutosaveMonthlyCollectionDate', {
                isEditFrequency,
            });
            return;
        }
        const day = state === 'FIRST_WORKING_DAY' ? 1 : -1;
        const rrule = new RRule({
            bymonthday: day,
            freq: RRule.MONTHLY,
        }).toString();
        if (hasOnboardingState) {
            dispatch(savePotsOnboardingState({ collectionFrequency: { rrule } }));
            navigateToNextScreen();
            return;
        }
        if (autosaveId) {
            if (state === autosaveCollectionMonthlyRRule?.type) {
                navigation.popToTop();
                return;
            }
            if (isEditFrequency && rules.length) {
                updateCollectionFrequency(autosaveId, rrule, rules);
                return;
            }
        }
        navigation.navigateDeprecated('AutosaveChooseSavingRules', {
            collectionFrequency: {
                rrule,
            },
        });
    }, [
        autosaveCollectionMonthlyRRule?.type,
        autosaveId,
        hasOnboardingState,
        isEditFrequency,
        navigateToNextScreen,
        rules,
        state,
        updateCollectionFrequency,
    ]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title={hasOnboardingState ? 'Select your collection date' : 'Monthly frequency'} subTitle="Select which day of the month to save"/>
          <CardView noMarginTop>
            <SettingsItemWithCheckBox style={styles.title} title="First working day of the month" selected={state === 'FIRST_WORKING_DAY'} onPress={onSelectPeriod('FIRST_WORKING_DAY')}/>
            <SettingsItemWithCheckBox style={styles.title} title="Last working day of the month" selected={state === 'LAST_WORKING_DAY'} onPress={onSelectPeriod('LAST_WORKING_DAY')}/>
            <SettingsItemWithCheckBox title="Fixed monthly date" selected={state === 'FIXED_MONTHLY_DATE'} description="e.g. on the 15th of the month" onPress={onSelectPeriod('FIXED_MONTHLY_DATE')}/>
          </CardView>
        </Animated.ScrollView>
        <Button brand floating title="Continue" onPress={handleContinue} isFetching={fetching}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    title: {
        paddingVertical: rem(4),
    },
});
export default AutosaveChooseMonthlyFrequency;
